import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { MapPin, Award, Crown, Sparkles, Gift, Package, TrendingUp } from "lucide-react";
import { t } from "@/i18n";

const MEDAL_COLORS = ["#FFD700", "#C0C0C0", "#CD7F32"];
const MEDAL_BG = ["rgba(255,215,0,0.12)", "rgba(192,192,192,0.1)", "rgba(205,127,50,0.1)"];
const PODIUM_RING = ["#FFD700", "#60A5FA", "#F97316"];
const PODIUM_BG = ["rgba(255,215,0,0.15)", "rgba(96,165,250,0.12)", "rgba(249,115,22,0.12)"];

const DISPLAY_NAMES = { "Koebenhavn": "København" };
const displayName = (name) => DISPLAY_NAMES[name] || name;

// Gecko-navne kan have suffix "(KBH 611534)" — strip ved image-lookup.
// Bruger item.image fra backend hvis tilgængeligt (pre-resolved af backend).
// Falder tilbage på memberImages-opslag med progressivt suffix-trim.
const lookupImage = (memberImages, name, preResolved) => {
  if (preResolved) return preResolved;
  if (!name || !memberImages) return undefined;
  if (memberImages[name]) return memberImages[name];
  // Strip parentes-suffix
  const base = name.replace(/\s*\([^)]*\)\s*$/, "").trim();
  if (memberImages[base]) return memberImages[base];
  // Progressivt trim bageste navneled: "Anne Sofie Holch Herskind" → "Anne Sofie Holch" → "Anne Sofie"
  const parts = base.split(" ");
  for (let end = parts.length - 1; end >= 2; end--) {
    const candidate = parts.slice(0, end).join(" ");
    if (memberImages[candidate]) return memberImages[candidate];
  }
  return undefined;
};

const shortName = (name) => {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length < 2) return name;
  return `${parts[0]} ${parts[parts.length - 1][0]}.`;
};

const getInitials = (name) => {
  const parts = name.split(" ").filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return name.slice(0, 2).toUpperCase();
};

const PodiumAvatar = ({ name, rank, size = "lg", imageUrl }) => {
  const idx = rank - 1;
  const sizeClass = size === "xl" ? "w-28 h-28" : size === "lg" ? "w-20 h-20" : "w-14 h-14";
  const textSize = size === "xl" ? "text-2xl" : size === "lg" ? "text-xl" : "text-sm";
  const borderW = size === "xl" ? "4px" : size === "lg" ? "3px" : "2px";
  const API = process.env.REACT_APP_BACKEND_URL || "";

  return (
    <div className={`relative ${sizeClass} shrink-0`}>
      {imageUrl ? (
        <img
          src={`${API}${imageUrl}`}
          alt={name}
          className={`${sizeClass} rounded-full object-cover aspect-square shrink-0 block`}
          style={{
            border: `${borderW} solid ${PODIUM_RING[idx]}`,
            boxShadow: rank === 1 ? `0 0 24px ${PODIUM_RING[idx]}30` : "none",
          }}
          onError={(e) => { e.target.style.display = "none"; e.target.nextSibling.style.display = "flex"; }}
        />
      ) : null}
      <div
        className={`${sizeClass} rounded-full items-center justify-center font-bold ${textSize} tracking-tight aspect-square shrink-0`}
        style={{
          display: imageUrl ? "none" : "flex",
          background: `linear-gradient(135deg, ${PODIUM_BG[idx]}, rgba(255,255,255,0.05))`,
          border: `${borderW} solid ${PODIUM_RING[idx]}`,
          color: PODIUM_RING[idx],
          boxShadow: rank === 1 ? `0 0 24px ${PODIUM_RING[idx]}30` : "none",
        }}
      >
        {getInitials(name)}
      </div>
      {rank === 1 && (
        <>
          <Sparkles
            className="absolute -top-2 -left-1 w-4 h-4 text-amber-400 animate-pulse-glow"
            style={{ animationDelay: "0s" }}
          />
          <Sparkles
            className="absolute -top-1 -right-2 w-3.5 h-3.5 text-amber-400 animate-pulse-glow"
            style={{ animationDelay: "0.5s" }}
          />
        </>
      )}
      <div
        className="absolute -bottom-1.5 left-1/2 -translate-x-1/2 w-6 h-6 rounded-full flex items-center justify-center text-[13px] font-bold text-black"
        style={{ backgroundColor: PODIUM_RING[idx] }}
      >
        {rank}
      </div>
    </div>
  );
};

export const TopSellersPodium = ({ students, memberImages = {}, periodSuffix = "denne uge", lang = "da", egenPlacering = null }) => {
  if (!students || students.length === 0) return null;

  // Reorder: #2 left, #1 center, #3 right (fill with nulls if fewer than 3)
  const s = [...students];
  while (s.length < 3) s.push(null);
  const ordered = [s[1], s[0], s[2]];

  return (
    <Card
      className="bg-card border-border shadow-none animate-fade-in-up opacity-0 stagger-1"
      data-testid="top-sellers-podium"
    >
      <CardHeader className="pb-3">
        <div className="flex items-center gap-2.5">
          <Crown className="w-5 h-5 text-[#FFD700]" />
          <CardTitle className="text-base font-bold tracking-tight">{t(lang, "topStudentsOverall")}</CardTitle>
        </div>
        <p className="text-sm text-muted-foreground mt-1">{t(lang, "topStudentsSubtitle", periodSuffix)}</p>
      </CardHeader>
      <CardContent>
        <div className="flex items-end justify-center gap-6 sm:gap-16 pt-6 pb-4">
          {ordered.map((student, i) => {
            const isCenter = i === 1;
            const posRank = isCenter ? 1 : i === 0 ? 2 : 3;  // søjle-position (ring-farve)
            if (!student) return (
              <div key={`empty-${posRank}`} className={`flex flex-col items-center ${isCenter ? "mb-6" : "mb-0"}`}>
                <div
                  className={`${isCenter ? "w-28 h-28" : "w-20 h-20"} rounded-full flex items-center justify-center aspect-square shrink-0`}
                  style={{ border: `${isCenter ? "4px" : "3px"} solid ${PODIUM_RING[posRank - 1]}30` }}
                />
                <span className="mt-5 text-muted-foreground text-[13px]">—</span>
              </div>
            );
            // Vis ELEVENS faktiske competition-rank (delt plads), ikke søjle-position.
            const visRank = student.rank ?? posRank;
            const erDelt = (students.filter(x => x.rank === visRank).length > 1);
            const imageUrl = lookupImage(memberImages, student.name, student.image);
            return (
              <div
                key={student.name}
                className={`flex flex-col items-center ${isCenter ? "mb-6" : "mb-0"}`}
                data-testid={`podium-rank-${visRank}`}
              >
                <PodiumAvatar name={student.name} rank={posRank} size={isCenter ? "xl" : "lg"} imageUrl={imageUrl} />
                <span
                  className={`mt-5 text-foreground text-center leading-tight ${isCenter ? "text-base font-bold" : "text-sm font-medium"}`}
                  style={{ maxWidth: isCenter ? "120px" : "100px" }}
                >
                  {shortName(student.name)}
                </span>
                <span
                  className={`font-kpi mt-1.5 ${isCenter ? "text-3xl" : "text-xl"}`}
                  style={{ color: PODIUM_RING[posRank - 1] }}
                >
                  {student.quantity}
                </span>
                {erDelt && (
                  <span className="text-[11px] text-muted-foreground mt-0.5">delt {visRank}. plads</span>
                )}
              </div>
            );
          })}
        </div>
        {/* Opfølgning #34 (#4): elevens egen placering hvis ikke i top 3 */}
        <MinPlacering placering={egenPlacering} />
      </CardContent>
    </Card>
  );
};

// Lille linje "Du er nr. X" — vises kun når eleven IKKE er i top 3 og har solgt >0.
function MinPlacering({ placering }) {
  if (!placering || !placering.rank || placering.rank <= 3) return null;
  return (
    <div className="mt-2 pt-3 border-t border-border text-center">
      <span className="text-sm font-semibold text-[#FF2A6D]">Du er nr. {placering.rank}</span>
      {placering.quantity != null && (
        <span className="text-[13px] text-muted-foreground"> · {placering.quantity}</span>
      )}
    </div>
  );
}

// Competition-ranking (delt plads: 1,1,1,4) — Opfølgning #34 (#4).
// Returnerer alle elever med rank <= maxRank (så delte top-placeringer alle vises).
function _competitionTop(entries, maxRank = 3) {
  const sorted = entries
    .filter(([, q]) => q > 0)
    .sort((a, b) => b[1] - a[1]);
  return sorted
    .map(([name, quantity]) => ({
      name, quantity,
      rank: 1 + sorted.filter(([, q]) => q > quantity).length,
    }))
    .filter(x => x.rank <= maxRank);
}

export const getOverallTopStudents = (rankings, count = 3) => {
  const allStudents = {};
  for (const city of Object.keys(rankings || {})) {
    for (const student of rankings[city]) {
      allStudents[student.name] = (allStudents[student.name] || 0) + student.quantity;
    }
  }
  return _competitionTop(Object.entries(allStudents), count);
};

export const getCombinedTopStudents = (rankingsList, count = 3) => {
  const allStudents = {};
  for (const rankings of rankingsList) {
    for (const city of Object.keys(rankings || {})) {
      for (const student of rankings[city]) {
        allStudents[student.name] = (allStudents[student.name] || 0) + student.quantity;
      }
    }
  }
  return _competitionTop(Object.entries(allStudents), count);
};

const CATEGORY_ICONS = {
  gavekort: Gift,
  produktsalg: Package,
  opgraderinger: TrendingUp,
};

export const TopCategoryPodium = ({ title, rankings, memberImages = {}, periodSuffix = "denne uge", egenPlacering = null }) => {
  const top = getOverallTopStudents(rankings || {});
  if (!top || top.length === 0) return null;

  const Icon = CATEGORY_ICONS[title.toLowerCase()] || Award;
  const s = [...top];
  while (s.length < 3) s.push(null);
  const ordered = [s[1], s[0], s[2]];

  return (
    <Card className="bg-card border-border shadow-none animate-fade-in-up opacity-0 stagger-2">
      <CardHeader className="pb-3">
        <div className="flex items-center gap-2.5">
          <Icon className="w-5 h-5 text-[#FFD700]" />
          <CardTitle className="text-base font-bold tracking-tight">{title}</CardTitle>
        </div>
        <p className="text-sm text-muted-foreground mt-1">Antal {periodSuffix}</p>
      </CardHeader>
      <CardContent>
        <div className="flex items-end justify-center gap-6 sm:gap-16 pt-6 pb-4">
          {ordered.map((student, i) => {
            const isCenter = i === 1;
            const posRank = isCenter ? 1 : i === 0 ? 2 : 3;
            if (!student) return (
              <div key={`empty-${posRank}`} className={`flex flex-col items-center ${isCenter ? "mb-6" : "mb-0"}`}>
                <div
                  className={`${isCenter ? "w-28 h-28" : "w-20 h-20"} rounded-full flex items-center justify-center aspect-square shrink-0`}
                  style={{ border: `${isCenter ? "4px" : "3px"} solid ${PODIUM_RING[posRank - 1]}30` }}
                />
                <span className="mt-5 text-muted-foreground text-[13px]">—</span>
              </div>
            );
            const visRank = student.rank ?? posRank;
            const erDelt = (top.filter(x => x.rank === visRank).length > 1);
            const imageUrl = lookupImage(memberImages, student.name, student.image);
            return (
              <div key={student.name} className={`flex flex-col items-center ${isCenter ? "mb-6" : "mb-0"}`}>
                <PodiumAvatar name={student.name} rank={posRank} size={isCenter ? "xl" : "lg"} imageUrl={imageUrl} />
                <span
                  className={`mt-5 text-foreground text-center leading-tight ${isCenter ? "text-base font-bold" : "text-sm font-medium"}`}
                  style={{ maxWidth: isCenter ? "120px" : "100px" }}
                >
                  {shortName(student.name)}
                </span>
                <span
                  className={`font-kpi mt-1.5 ${isCenter ? "text-3xl" : "text-xl"}`}
                  style={{ color: PODIUM_RING[posRank - 1] }}
                >
                  {student.quantity}
                </span>
                {erDelt && (
                  <span className="text-[11px] text-muted-foreground mt-0.5">delt {visRank}. plads</span>
                )}
              </div>
            );
          })}
        </div>
        <MinPlacering placering={egenPlacering} />
      </CardContent>
    </Card>
  );
};

export const StudentRanking = ({ rankings, memberImages = {}, periodSuffix = "denne uge", lang = "da" }) => {
  const cities = Object.keys(rankings || {}).sort();

  return (
    <div className="space-y-6">
      {/* Per-city rankings */}
      <Card
        className="bg-card border-border shadow-none animate-fade-in-up opacity-0 stagger-3"
        data-testid="student-ranking-card"
      >
        <CardHeader className="pb-4">
          <div className="flex items-center gap-2">
            <Award className="w-4 h-4 text-[#FF2A6D]" />
            <CardTitle className="text-sm font-bold tracking-tight">
              {t(lang, "topStudentsByCity")}
            </CardTitle>
          </div>
          <p className="text-[13px] text-muted-foreground mt-1">{t(lang, "topStudentsByCitySubtitle", periodSuffix)}</p>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3 sm:gap-4">
            {cities.map((city) => (
            <div
              key={city}
              className="rounded-xl bg-secondary/50 border border-border p-4 hover:border-border transition-colors duration-200"
              data-testid={`city-ranking-${city.toLowerCase()}`}
            >
              <div className="flex items-center gap-2 mb-4">
                <MapPin className="w-3.5 h-3.5 text-[#FF2A6D]" />
                <span className="text-[13px] font-bold text-foreground uppercase tracking-wider">
                  {displayName(city)}
                </span>
              </div>
              <div className="space-y-2.5">
                {rankings[city].map((student, idx) => (
                  <div
                    key={student.name}
                    className="flex items-center gap-3"
                    data-testid={`student-rank-${city.toLowerCase()}-${idx + 1}`}
                  >
                    <div
                      className="w-6 h-6 rounded-full flex items-center justify-center text-[13px] font-bold shrink-0"
                      style={{
                        backgroundColor: MEDAL_BG[idx],
                        color: MEDAL_COLORS[idx],
                      }}
                    >
                      {idx + 1}
                    </div>
                    <div className="flex-1 min-w-0">
                      <span className="text-sm text-foreground truncate block">
                        {shortName(student.name)}
                      </span>
                    </div>
                    <span
                      className="text-sm font-kpi shrink-0"
                      style={{ color: MEDAL_COLORS[idx] }}
                    >
                      {student.quantity}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
    </div>
  );
};
